import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import Spinner from '../components/Spinner';
import { useTickets } from '../contexts/TicketsProvider';
import { STATUSES } from '../lib/constants';
import TicketCard from '../components/TicketCard';
import ValidationDialog from '../components/ValidationDialog';
import { cn } from '../lib/utils';

const sortColumn = (a, b) =>
  (a.position - b.position) || (new Date(b.created_at) - new Date(a.created_at));

// The board is for active work. "done" is an archive — it lives in the tickets
// view (and planning), not as a column here.
const BOARD_STATUSES = STATUSES.filter((s) => s.key !== 'done');

// Card density. Compact (default) fits roughly twice as many cards per column;
// cozy is the original roomy card. Remembered per browser.
const DENSITIES = ['compact', 'cozy'];

export default function BoardPage() {
  const { visibleTickets, tickets, members, loading, moveTicket, requestValidation, assignMe, openEditTicket, openNewTicket, scope, myId, subtaskStats } = useTickets();
  // Team view shows the parent tickets (subtasks live inside them); "mine"
  // also surfaces the subtasks assigned to you, so your own work is on the board.
  const boardTickets = useMemo(() => (
    visibleTickets.filter((t) => !t.parent_id || scope === 'mine')
  ), [visibleTickets, scope]);
  // Only devs are assignable, so only show "take it" to a dev (matches the modal
  // and the backend guard — avoids a 403 for non-dev users).
  const meAssignable = !!members.find((m) => m.id === myId)?.is_assignable;
  const draggedId = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  // When a card is dropped into the Validation column we first ask for a note.
  const [validating, setValidating] = useState(null);  // { ticket, position }
  const [vBusy, setVBusy] = useState(false);
  const [density, setDensity] = useState(() => (
    DENSITIES.includes(localStorage.getItem('ticky-density')) ? localStorage.getItem('ticky-density') : 'compact'
  ));
  useEffect(() => { localStorage.setItem('ticky-density', density); }, [density]);
  const compact = density === 'compact';

  // Moving into Validation goes through the note dialog + dedicated endpoint;
  // everything else (incl. reordering *within* Validation) is a plain move.
  const dropInto = (id, status, position) => {
    if (status === 'validation') {
      const cur = tickets.find((t) => t.id === id);
      if (!cur || cur.status !== 'validation') {
        setValidating({ ticket: cur, position });
        return;
      }
    }
    moveTicket(id, { status, position });
  };

  const handleValidate = async ({ message, images }) => {
    if (!validating) return;
    setVBusy(true);
    try {
      await requestValidation(validating.ticket.id, { message, position: validating.position, images });
      setValidating(null);
    } catch (e) {
      console.error(e);
    } finally {
      setVBusy(false);
    }
  };

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(STATUSES.map((s) => [s.key, []]));
    for (const t of boardTickets) {
      if (!map[t.status]) map[t.status] = [];
      map[t.status].push(t);
    }
    for (const key of Object.keys(map)) map[key].sort(sortColumn);
    return map;
  }, [boardTickets]);

  const onDragStart = (id) => (e) => {
    draggedId.current = id;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox needs data set for drag to start.
    e.dataTransfer.setData('text/plain', String(id));
  };

  const onDragEnd = () => {
    draggedId.current = null;
    setDraggingId(null);
    setOverCol(null);
  };

  // Drop on empty column space -> append to the end of that column.
  const onDropToColumn = (status) => (e) => {
    e.preventDefault();
    setOverCol(null);
    const id = draggedId.current;
    if (id == null) return;
    const list = byStatus[status] || [];
    const last = list[list.length - 1];
    const position = last ? last.position + 1 : 0;
    dropInto(id, status, position);
  };

  // Drop onto a card -> place the dragged card just before it.
  const onDropOnCard = (target) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOverCol(null);
    const id = draggedId.current;
    if (id == null || id === target.id) return;
    const list = byStatus[target.status] || [];
    const idx = list.findIndex((t) => t.id === target.id);
    const prev = list[idx - 1];
    const position = prev ? (prev.position + target.position) / 2 : target.position - 1;
    dropInto(id, target.status, position);
  };

  // One-click status change from a card (e.g. "✓ done") — appends to the end of
  // the target column, so you don't have to drag to an off-screen column.
  // Position is computed from ALL tickets in that column (not the scope-filtered
  // view), so it lands at the true end regardless of the team/mine toggle.
  const moveToStatus = (id, status) => {
    const maxPos = tickets
      .filter((t) => t.status === status)
      .reduce((m, t) => Math.max(m, t.position), 0);
    moveTicket(id, { status, position: maxPos + 1 });
  };

  return (
    <div className={cn('h-full overflow-x-auto', compact ? 'p-4' : 'p-6')}>
      {loading && boardTickets.length === 0 ? (
        <Spinner />
      ) : (
        <>
          <div className="mb-2 flex items-center justify-end gap-2 text-xs text-muted-foreground">
            <span>density</span>
            <div className="flex overflow-hidden rounded-md border border-border" title="Compact fits more cards per column; cozy shows the full card">
              {DENSITIES.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDensity(d)}
                  className={density === d
                    ? 'bg-primary px-2 py-0.5 font-medium text-primary-foreground'
                    : 'px-2 py-0.5 transition hover:bg-accent'}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          {boardTickets.length === 0 ? (
            <div className="mb-4 rounded-xl border border-dashed border-border bg-card p-6 text-center">
              <p className="text-sm font-medium">
                {scope === 'mine' ? 'Nothing assigned to you' : 'No tickets yet'}
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                {scope === 'mine'
                  ? 'Switch to “team” to see everyone’s tickets, or take one from the board.'
                  : 'Create your first ticket with the “New ticket” button.'}
              </p>
              <div className="mt-3 flex justify-center">
                <Button onClick={() => openNewTicket('new')} className="gap-1.5">
                  <Plus className="h-4 w-4" /> New ticket
                </Button>
              </div>
            </div>
          ) : null}
          <div className={cn('flex min-h-[60vh] [justify-content:safe_center]', compact ? 'gap-3' : 'gap-4')}>
          {BOARD_STATUSES.map((col) => {
            const list = byStatus[col.key] || [];
            return (
              <section
                key={col.key}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOverCol(col.key);
                }}
                onDrop={onDropToColumn(col.key)}
                className={cn(
                  // Flex to fill the width: columns share the available space and
                  // shrink to a readable minimum so all of them fit without a
                  // horizontal scroll; on wide screens they grow to use it all.
                  'flex min-w-[11.5rem] flex-1 flex-col rounded-xl border border-border bg-muted/40 transition-colors',
                  overCol === col.key && 'border-primary/50 bg-primary/5',
                )}
              >
                <div className={cn('flex items-center justify-between', compact ? 'px-2.5 py-2' : 'px-3 py-2.5')}>
                  <div className="flex items-center gap-2">
                    <span className={cn('h-2.5 w-2.5 rounded-full', col.dot)} />
                    <span className={cn('text-sm font-semibold', col.header)}>{col.key}</span>
                    <span className="rounded bg-muted px-1.5 text-xs text-muted-foreground">{list.length}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => openNewTicket(col.key)}
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label={`Add ticket to ${col.label}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <div className={cn('flex flex-1 flex-col', compact ? 'gap-1.5 px-1.5 pb-2' : 'gap-2 px-2 pb-3')}>
                  {list.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      compact={compact}
                      subtasks={subtaskStats[ticket.id]}
                      dragging={draggingId === ticket.id}
                      onDragStart={onDragStart(ticket.id)}
                      onDragEnd={onDragEnd}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={onDropOnCard(ticket)}
                      onClick={() => openEditTicket(ticket)}
                      onTake={meAssignable ? () => assignMe(ticket.id) : undefined}
                      onComplete={() => moveToStatus(ticket.id, 'done')}
                      onReopen={() => moveToStatus(ticket.id, 'todo')}
                      myId={myId}
                    />
                  ))}
                  {list.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => openNewTicket(col.key)}
                      className="rounded-md border border-dashed border-border py-6 text-xs text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
                    >
                      // drop here or click to add
                    </button>
                  ) : null}
                </div>
              </section>
            );
          })}
          </div>
        </>
      )}
      <ValidationDialog
        open={!!validating}
        ticket={validating?.ticket}
        busy={vBusy}
        onConfirm={handleValidate}
        onCancel={() => { if (!vBusy) setValidating(null); }}
      />
    </div>
  );
}
